import { useMemo } from 'react';
import type { ApexOptions } from 'apexcharts';
import {
    buildBrandedTooltip,
    buildChartPalette,
    buildPlainTooltip,
    buildValueFormatter,
    mergeOptions,
    type ResolvedConfig,
    variantOptionsAreaBuilder,
    variantOptionsBarBuilder,
    variantOptionsLineBuilder,
} from '@/components/core-components/StanCharts/chart';

export interface UseApexChartProps {
    variant: 'bar' | 'line' | 'area';
    series?: ApexOptions['series'];
    customOptions?: ApexOptions;
    chartPrefix?: string;
    dataSuffix?: string;
    decimals?: number;
    stacked?: boolean;
    tooltipFormatter?: (val: number | null) => string;
    showStanBrandingInTooltip?: boolean;
    includeTooltipMarker?: boolean;
    categories?: (string | number)[];
    yAxisMin?: number;
    yAxisMax?: number;
    paletteConfig: {
        defaultColors: string[];
        defaultColor: string;
        seriesColors?: string[];
        customColor?: string;
    };
    brandedTooltipClassName?: string;
    // Bar chart specific options
    borderRadius?: number;
    // Line chart specific options
    enableZoom?: boolean;
    stroke?: ApexOptions['stroke'];
    markers?: ApexOptions['markers'];
}

interface InternalResolvedConfig extends ResolvedConfig {
    resolvedSeries: ApexOptions['series'];
    showBranding: boolean;
    resolvedDecimals: number;
    useAbbreviation: boolean;
}

export interface UseApexChartResult {
    options: ApexOptions;
    resolvedSeries: ApexOptions['series'];
    chartType: NonNullable<ApexOptions['chart']>['type'];
}

/**
 * Shared hook for building ApexCharts configurations.
 * Abstracts common logic for palette, tooltips, formatting.
 * Each chart type provides its own variant-specific builder for customization.
 *
 * @example
 * ```ts
 * const result = useApexChart({
 *   variant: 'bar',
 *   series: myData,
 *   paletteConfig: { defaultColors: ['#123'], defaultColor: '#123' },
 *   borderRadius: 5,
 * });
 * ```
 */
export function useApexChart({
    variant,
    series,
    customOptions,
    chartPrefix = '',
    dataSuffix = '',
    decimals,
    stacked = false,
    tooltipFormatter,
    showStanBrandingInTooltip,
    includeTooltipMarker = false,
    paletteConfig,
    brandedTooltipClassName = '',
    categories,
    yAxisMin,
    yAxisMax,
    // Bar chart specific options
    borderRadius,
    // Line chart specific options
    enableZoom,
    stroke,
    markers,
}: UseApexChartProps): UseApexChartResult {
    /**
     * Step 1: Resolve configuration primitives from props.
     * Builds color palette, value formatter, and other derived values.
     */
    const resolved = useMemo<InternalResolvedConfig>(() => {
        const resolvedDecimals = decimals ?? 2;
        const resolvedSeries = series ?? [];
        const showBranding = showStanBrandingInTooltip ?? false;

        // Extract series colors from data
        const seriesPalette =
            paletteConfig.seriesColors ??
            resolvedSeries
                .map(entry => (typeof entry === 'object' ? entry.color : undefined))
                .filter((value): value is string => Boolean(value));

        // Build color palette using utility
        const palette = buildChartPalette(
            seriesPalette,
            paletteConfig.customColor,
            paletteConfig.defaultColors,
            paletteConfig.defaultColor,
            stacked
        );

        // Determine if we should use abbreviation (1000 → 1K)
        const useAbbreviation = !tooltipFormatter;

        // Create value formatter using utility
        const formatValue = buildValueFormatter(
            tooltipFormatter,
            chartPrefix,
            dataSuffix,
            resolvedDecimals,
            useAbbreviation
        );

        return {
            chartType: variant, // Derive chartType from variant
            resolvedSeries,
            showBranding,
            palette,
            formatValue,
            resolvedDecimals,
            useAbbreviation,
        };
    }, [
        decimals,
        variant,
        series,
        showStanBrandingInTooltip,
        stacked,
        tooltipFormatter,
        chartPrefix,
        dataSuffix,
        paletteConfig.defaultColors,
        paletteConfig.defaultColor,
        paletteConfig.seriesColors,
        paletteConfig.customColor,
    ]);

    /**
     * Step 2: Build tooltip configuration (branded or plain).
     */
    const tooltip = useMemo(() => {
        if (resolved.showBranding) {
            return buildBrandedTooltip(resolved.formatValue, brandedTooltipClassName);
        }
        return buildPlainTooltip(resolved.formatValue, { includeMarker: includeTooltipMarker });
    }, [resolved.showBranding, resolved.formatValue, brandedTooltipClassName, includeTooltipMarker]);

    /**
     * Step 3: Build variant-specific options using conditional logic.
     * This allows each chart type to fully customize its behavior.
     */
    const variantOptions = useMemo<ApexOptions>(() => {
        let variantSpecific: ApexOptions;

        // Use conditional logic to determine which variant builder to use
        if (variant === 'bar') {
            variantSpecific = variantOptionsBarBuilder(resolved, {
                stacked,
                categories,
                yAxisMin,
                yAxisMax,
                borderRadius,
            });
        } else if (variant === 'line') {
            variantSpecific = variantOptionsLineBuilder(resolved, {
                stacked,
                categories,
                yAxisMin,
                yAxisMax,
                enableZoom,
                stroke,
                markers,
            });
        } else if (variant === 'area') {
            variantSpecific = variantOptionsAreaBuilder(resolved, {
                stacked,
                categories,
                yAxisMin,
                yAxisMax,
                stroke,
            });
        } else {
            throw new Error(`Unsupported chart variant: ${variant}`);
        }

        // If variant doesn't provide its own tooltip, use the default one
        if (!variantSpecific.tooltip) {
            return {
                ...variantSpecific,
                tooltip,
            };
        }

        return variantSpecific;
    }, [
        variant,
        resolved,
        tooltip,
        stacked,
        categories,
        yAxisMin,
        yAxisMax,
        borderRadius,
        enableZoom,
        stroke,
        markers,
    ]);

    /**
     * Step 4: Final merge with custom overrides.
     * Priority: variantOptions < customOptions
     */
    const options = useMemo(() => mergeOptions(variantOptions, customOptions), [variantOptions, customOptions]);

    return {
        options,
        resolvedSeries: resolved.resolvedSeries,
        chartType: resolved.chartType,
    };
}
